import { MemoryStorageBackend, NoStorageBackend } from './backends'
import { LocalStorageBackend, SessionStorageBackend } from './browser-backends'

export interface StorageBackend {
  get(key: string): any

  set(key: string, value: any): void

  remove(key: string): void

  clear(): void
}

export enum StorageBackendType {
  LocalStorage = 'local',
  SessionStorage = 'session',
  MemoryStorage = 'memory',
  NoStorage = 'disable',
}

export class Storages {
  private _general: StorageBackend //persistent storage
  private _widgets: StorageBackend //per widget persistent storage
  private _session: StorageBackend //per session store

  private _page: StorageBackend = new MemoryStorageBackend() // per page storage
  private _static: StorageBackend = new MemoryStorageBackend() //static storage

  constructor(backend: string = StorageBackendType.LocalStorage, prefix = 'gsc') {
    let storageCreator: (name: string) => StorageBackend = () => new MemoryStorageBackend()
    if (backend === StorageBackendType.LocalStorage) {
      if (!LocalStorageBackend.isSupported()) {
        console.warn('LocalStorage not supported')
      } else {
        storageCreator = (name: string) => new LocalStorageBackend(name)
      }
    } else if (backend === StorageBackendType.SessionStorage) {
      if (!SessionStorageBackend.isSupported()) {
        console.warn('SessionStorage not supported')
      } else {
        storageCreator = (name: string) => new SessionStorageBackend(name)
      }
    } else if (backend === StorageBackendType.MemoryStorage) {
      storageCreator = () => new MemoryStorageBackend()
    } else if (backend === StorageBackendType.NoStorage) {
      storageCreator = () => new NoStorageBackend()
    } else {
      throw new Error(`Unknown storage backend ${backend}`)
    }

    this._general = storageCreator(prefix)
    this._widgets = storageCreator(prefix + 'w')
    this._session = storageCreator(prefix + 's')
  }

  public save(key: string, value: any) {
    this._general.set(key, value)
  }

  public load(key: string): any {
    return this._general.get(key)
  }

  //todo: widgets store metrics only, make more friendly interface
  public saveFor(widgetId: number, key: string, value: any) {
    const wKey = widgetId.toString()
    let data = this._widgets.get(wKey)
    if (typeof data !== 'object' || !data) {
      data = {}
    }
    data[key] = value
    data.$last = new Date().getTime() //save last widget interaction
    this._widgets.set(wKey, data)
  }
  public loadAllFor(widgetId: number) {
    const wKey = widgetId.toString()
    return this._widgets.get(wKey)
  }
  public loadFor(widgetId: number, key: string) {
    const data = this.loadAllFor(widgetId)
    if (data) {
      return data[key]
    }
  }

  protected cleanupWidgets(later: number) {
    //todo: remove outdated base on $last, ex: > 1month
  }

  public newSession(id: string, startTime: Date) {
    this._session.clear()
    this._session.set('id', id)
    this._session.set('$start', startTime.getTime())
  }

  public saveSession(key: string, value: any) {
    this._session.set(key, value)
  }

  public loadSession(key: string): any {
    return this._session.get(key)
  }

  public cleanupSession() {
    this._session.clear()
  }

  public get general(): StorageBackend {
    return this._general
  }

  public get widgets(): StorageBackend {
    return this._widgets
  }

  public get session(): StorageBackend {
    return this._session
  }

  public get page(): StorageBackend {
    return this._page
  }

  public get static(): StorageBackend {
    return this._static
  }
}
